import React, { useState, useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';

export default function SubmitIdea() {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    domain: 'product',
    workType: 'Product',
    riskComplianceSecurityFlag: false,
    submitterName: '',
    submitterEmail: '',
  });

  // Auto-populate name and email from authenticated user
  useEffect(() => {
    if (user?.Name && user?.Email) {
      setFormData(prev => ({
        ...prev,
        submitterName: user.Name,
        submitterEmail: user.Email,
      }));
    }
  }, [user]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [submissionId, setSubmissionId] = useState('');

  const domains = [
    { value: 'infrastructure', label: 'Infrastructure' },
    { value: 'networking', label: 'Networking' },
    { value: 'voice', label: 'Voice' },
    { value: 'product', label: 'Product' },
    { value: 'development', label: 'Development' },
  ];

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    if (type === 'checkbox') {
      setFormData(prev => ({
        ...prev,
        [name]: (e.target as HTMLInputElement).checked,
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      if (!formData.title.trim()) throw new Error('Idea title is required');
      if (!formData.description.trim()) throw new Error('Project description is required');
      if (!formData.submitterName.trim()) throw new Error('Your name is required');
      if (!formData.submitterEmail.includes('@')) throw new Error('Valid email is required');

      const res = await fetch('/projects', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: formData.title,
          description: formData.description,
          domain: formData.domain,
          workType: formData.workType,
          riskComplianceSecurityFlag: formData.riskComplianceSecurityFlag,
          createdByEmail: formData.submitterEmail,
          createdByName: formData.submitterName,
        }),
      });

      const data = await res.json();
      if (!data.success) {
        throw new Error(data.error || 'Failed to create project');
      }

      setSubmissionId(data.data.ProjectId);
      setSuccess('Idea submitted successfully!');
      setFormData({
        title: '',
        description: '',
        domain: 'product',
        workType: 'Product',
        riskComplianceSecurityFlag: false,
        submitterName: '',
        submitterEmail: '',
      });
    } catch (err: any) {
      setError(err.message || 'Failed to submit idea');
    } finally {
      setLoading(false);
    }
  };

  if (success && submissionId) {
    return (
      <div className="container" style={{ maxWidth: '700px', margin: '0 auto', minHeight: '100vh', display: 'flex', alignItems: 'center' }}>
        <div style={{ width: '100%' }}>
          <div
            style={{
              background: '#d4edda',
              color: '#155724',
              padding: '2rem',
              borderRadius: '8px',
              textAlign: 'center',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            }}
          >
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>💡</div>
            <h2 style={{ margin: '0 0 1rem 0', color: '#155724' }}>Idea Submitted!</h2>
            <p style={{ fontSize: '1.1rem', marginBottom: '1.5rem' }}>
              Thank you for sharing your idea. It has been submitted successfully.
            </p>
            <p style={{ fontSize: '0.9rem', marginTop: '1.5rem', color: '#666' }}>
              Your idea is now available for review and community voting.
            </p>
          </div>

          <button
            onClick={() => window.location.href = '/#/ideas'}
            style={{
              width: '100%',
              marginTop: '2rem',
              padding: '0.75rem 1.5rem',
              background: '#0066cc',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontWeight: 600,
              fontSize: '1rem',
            }}
          >
            → View All Ideas
          </button>

          <button
            onClick={() => window.location.href = '/#/submit-idea'}
            style={{
              width: '100%',
              marginTop: '0.5rem',
              padding: '0.75rem 1.5rem',
              background: '#6c757d',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontWeight: 600,
              fontSize: '1rem',
            }}
          >
            + Submit Another Idea
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="container" style={{ maxWidth: '700px', margin: '0 auto' }}>
      <header className="header" style={{ marginBottom: '2rem' }}>
        <h1>Submit a Project Idea</h1>
        <p className="header-subtitle">Have an idea? We'd love to hear it!</p>
      </header>

      <div style={{ padding: '0 20px' }}>
        {error && (
          <div
            style={{
              backgroundColor: '#f8d7da',
              color: '#721c24',
              padding: '1rem',
              borderRadius: '4px',
              marginBottom: '1.5rem',
            }}
          >
            {error}
          </div>
        )}

        <form
          onSubmit={handleSubmit}
          style={{
            background: 'white',
            padding: '2rem',
            borderRadius: '8px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          }}
        >
          {/* Your Name */}
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>
              Your Name *
            </label>
            <input
              type="text"
              name="submitterName"
              value={formData.submitterName}
              onChange={handleChange}
              placeholder="e.g., John Smith"
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '1rem',
                boxSizing: 'border-box',
              }}
            />
          </div>

          {/* Your Email */}
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>
              Your Email *
            </label>
            <input
              type="email"
              name="submitterEmail"
              value={formData.submitterEmail}
              onChange={handleChange}
              placeholder="john@company.com"
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '1rem',
                boxSizing: 'border-box',
              }}
            />
            <p style={{ fontSize: '0.85rem', color: '#666', marginTop: '0.25rem' }}>
              We'll use this to contact you about your idea
            </p>
          </div>

          {/* Idea Title */}
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>
              Idea Title *
            </label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleChange}
              placeholder="e.g., Mobile App Redesign"
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '1rem',
                boxSizing: 'border-box',
              }}
            />
          </div>

          {/* Description */}
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>
              Description *
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder="What problem does this idea solve? Why is it important?"
              rows={4}
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '1rem',
                boxSizing: 'border-box',
                fontFamily: 'inherit',
              }}
            />
          </div>

          {/* Domain */}
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>
              Area/Domain *
            </label>
            <select
              name="domain"
              value={formData.domain}
              onChange={handleChange}
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '1rem',
              }}
            >
              {domains.map(d => (
                <option key={d.value} value={d.value}>
                  {d.label}
                </option>
              ))}
            </select>
          </div>

          {/* Work Type */}
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>
              Type of Work *
            </label>
            <div style={{ display: 'flex', gap: '2rem' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <input
                  type="radio"
                  name="workType"
                  value="Product"
                  checked={formData.workType === 'Product'}
                  onChange={handleChange}
                />
                Product Initiative
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <input
                  type="radio"
                  name="workType"
                  value="NonProduct"
                  checked={formData.workType === 'NonProduct'}
                  onChange={handleChange}
                />
                Internal/Support
              </label>
            </div>
          </div>

          {/* Risk Flag */}
          <div style={{ marginBottom: '2rem' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <input
                type="checkbox"
                name="riskComplianceSecurityFlag"
                checked={formData.riskComplianceSecurityFlag}
                onChange={handleChange}
              />
              <span style={{ fontWeight: 600 }}>
                This involves data security, compliance, or enterprise risk
              </span>
            </label>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading}
            style={{
              width: '100%',
              padding: '0.75rem 1.5rem',
              background: '#28a745',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: loading ? 'not-allowed' : 'pointer',
              fontWeight: 600,
              fontSize: '1rem',
              opacity: loading ? 0.6 : 1,
            }}
          >
            {loading ? 'Submitting...' : '✓ Submit Your Idea'}
          </button>

          <p style={{ fontSize: '0.85rem', color: '#999', marginTop: '1.5rem', textAlign: 'center' }}>
            All submissions will be reviewed by our leadership team.
          </p>
        </form>
      </div>
    </div>
  );
}
